/**
 * Universal Bookmark Import & Export Utility for MeMark
 * 
 * Supports:
 * - Netscape Bookmark File format (Chrome, Edge, Firefox, Safari compatible HTML)
 * - JSON Backup & Restore format
 */

export function exportToJSON(data, settings) {
  const exportPayload = {
    version: '1.2',
    exportedAt: new Date().toISOString(),
    source: 'MeMark Studio',
    settings: {
      theme: settings?.theme || 'default',
      isDark: settings?.isDark ?? true,
      backgroundType: settings?.backgroundType || 'constellation',
      themeColors: settings?.themeColors || {},
    },
    data,
  };

  const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `memark-backup-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportToHTML(data) {
  const folders = Object.values(data).filter((f) => f.id !== 'root');
  const rootBookmarks = (data.root?.items || []).filter((i) => i.type === 'link');

  let html = `<!DOCTYPE NETSCAPE-Bookmark-file-1>
<!-- This is an automatically generated file.
     It will be read and overwritten.
     DO NOT EDIT! -->
<META HTTP-EQUIV="Content-Type" CONTENT="text/html; charset=UTF-8">
<TITLE>Bookmarks - MeMark</TITLE>
<H1>Bookmarks</H1>
<DL><p>
`;

  // Root bookmarks
  for (const b of rootBookmarks) {
    const addDate = Math.floor(Date.now() / 1000);
    html += `    <DT><A HREF="${b.url}" ADD_DATE="${addDate}">${escapeHtml(b.title || b.url)}</A>\n`;
  }

  // Folders
  for (const f of folders) {
    const addDate = Math.floor(Date.now() / 1000);
    html += `    <DT><H3 ADD_DATE="${addDate}">${escapeHtml(f.title || 'Folder')}</H3>\n    <DL><p>\n`;
    const items = (f.items || []).filter((i) => i.type === 'link');
    for (const b of items) {
      html += `        <DT><A HREF="${b.url}" ADD_DATE="${addDate}">${escapeHtml(b.title || b.url)}</A>\n`;
    }
    html += `    </DL><p>\n`;
  }

  html += `</DL><p>\n`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `memark-bookmarks-${new Date().toISOString().slice(0, 10)}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function parseNetscapeBookmarkHTML(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const results = {
    folders: [],
    rootBookmarks: [],
  };

  function walk(node, currentFolder) {
    let child = node.firstElementChild;
    while (child) {
      if (child.tagName === 'DT') {
        const h3 = child.querySelector(':scope > H3');
        const a = child.querySelector(':scope > A');
        const dl = child.querySelector(':scope > DL');

        if (h3 && dl) {
          const folderTitle = h3.textContent.trim();
          const folderObj = { title: folderTitle, bookmarks: [] };
          results.folders.push(folderObj);
          walk(dl, folderObj);
        } else if (a) {
          const title = a.textContent.trim() || a.href;
          const url = a.href;
          if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
            const bm = { title, url };
            if (currentFolder) {
              currentFolder.bookmarks.push(bm);
            } else {
              results.rootBookmarks.push(bm);
            }
          }
        }
      }
      child = child.nextElementSibling;
    }
  }

  const rootDl = doc.querySelector('DL');
  if (rootDl) {
    walk(rootDl, null);
  } else {
    // Fallback: extract all <a> tags directly
    const allLinks = doc.querySelectorAll('a[href]');
    allLinks.forEach((a) => {
      const url = a.href;
      if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
        results.rootBookmarks.push({
          title: a.textContent.trim() || url,
          url,
        });
      }
    });
  }

  return results;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
